import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { usePlanAccess } from '@/hooks/usePlanAccess';
import Header from './Header';

interface SidebarItem {
  label: string;
  path: string;
  icon: string;
  feature?: string;
  alwaysShow?: boolean;
}

export default function AdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { profile } = useAuth();
  const { hasFeature, isAdmin, isTrial } = usePlanAccess();

  // All possible sidebar items with feature gate
  const allSidebarItems: SidebarItem[] = useMemo(() => [
    { label: 'Dashboard', path: '/dashboard', icon: 'ri-dashboard-line', alwaysShow: true },
    { label: 'Contatos', path: '/contacts', icon: 'ri-contacts-book-line', feature: 'contacts', alwaysShow: true },
    { label: 'Conectar WhatsApp', path: '/tutorial/whatsapp', icon: 'ri-whatsapp-line', alwaysShow: true },
    { label: 'Como Importar', path: '/contacts/import-tutorial', icon: 'ri-guide-line', feature: 'contacts', alwaysShow: true },
    { label: 'Grupos', path: '/groups', icon: 'ri-group-line', feature: 'groups' },
    { label: 'Campanhas', path: '/campaigns', icon: 'ri-megaphone-line', feature: 'campaigns' },
    { label: 'Templates', path: '/templates', icon: 'ri-file-text-line', feature: 'templates' },
    { label: 'Meu Uso', path: '/my-usage', icon: 'ri-speed-up-line', feature: 'my_usage' },
    { label: 'Relatórios', path: '/reports', icon: 'ri-bar-chart-box-line', feature: 'reports' },
    { label: 'Configurações', path: '/settings', icon: 'ri-settings-3-line', feature: 'settings_general' },
    { label: 'Integrações', path: '/integrations', icon: 'ri-plug-line', feature: 'integrations' },
    { label: 'Notificações', path: '/notifications', icon: 'ri-notification-3-line', alwaysShow: true },
    { label: 'Suporte', path: '/support', icon: 'ri-customer-service-2-line', alwaysShow: true },
    { label: 'Logs', path: '/logs', icon: 'ri-file-list-3-line', feature: 'logs' },
    { label: 'Planos', path: '/subscription', icon: 'ri-vip-crown-line', alwaysShow: true },
    { label: 'Taxa Setup', path: '/setup-fee', icon: 'ri-tools-line', feature: 'setup_fee' },
    { label: 'Créditos', path: '/creditos', icon: 'ri-coin-line', alwaysShow: true },
    { label: 'Faturas', path: '/invoices', icon: 'ri-receipt-line', feature: 'invoices' },
    { label: 'Diagnóstico', path: '/diagnostic', icon: 'ri-stethoscope-line', feature: 'admin_panel' },
    { label: 'Implantação', path: '/deploy', icon: 'ri-rocket-2-line', feature: 'admin_panel' },
    { label: 'Segurança', path: '/security', icon: 'ri-shield-check-line', feature: 'admin_panel' },
    { label: 'Admin Planos', path: '/admin/plans', icon: 'ri-vip-crown-line', feature: 'admin_panel' },
    { label: 'Admin Usuários', path: '/admin/users', icon: 'ri-group-2-line', feature: 'admin_panel' },
    { label: 'Admin Dashboard', path: '/admin/dashboard', icon: 'ri-dashboard-3-line', feature: 'admin_panel' },
    { label: 'Admin Logs', path: '/admin/logs', icon: 'ri-file-list-3-line', feature: 'admin_panel' },
    { label: 'Admin Faturas', path: '/admin/invoices', icon: 'ri-bank-card-line', feature: 'admin_panel' },
    { label: 'Admin Erros', path: '/admin/errors', icon: 'ri-bug-line', feature: 'admin_panel' },
  ], []);

  // Filter by plan access
  const sidebarItems: SidebarItem[] = useMemo(() => {
    return allSidebarItems.filter((item) => {
      if (item.alwaysShow) return true;
      if (item.feature === 'admin_panel') return isAdmin;
      if (!item.feature) return true;
      return hasFeature(item.feature as Parameters<typeof hasFeature>[0]);
    });
  }, [allSidebarItems, hasFeature, isAdmin]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background-50 dark:bg-[#08080d]">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 dark:bg-black/60 backdrop-blur-[2px] z-40 lg:hidden transition-opacity"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-background-100 transition-all duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)] ${
          collapsed ? 'w-[76px]' : 'w-[264px]'
        } ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{
          boxShadow: mobileOpen ? '8px 0 32px rgba(0,0,0,0.08)' : 'none',
          borderRight: '1px solid oklch(var(--background-200))',
        }}
      >
        {/* Logo area */}
        <div className={`flex items-center h-[68px] px-4 border-b border-background-100/80 dark:border-background-200/80 ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0">
            <i className="ri-whatsapp-fill text-white text-lg" />
          </div>
          {!collapsed && (
            <span className="font-heading font-bold text-foreground-950 dark:text-foreground-100 text-[15px] tracking-tight whitespace-nowrap">
              WZap PRO
            </span>
          )}
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto py-4 px-2.5">
          <ul className="flex flex-col gap-1">
            {sidebarItems.map((item) => {
              const active = isActive(item.path);
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    className={`relative flex items-center gap-3 px-3 py-[10px] rounded-xl text-[13px] font-medium transition-all duration-200 whitespace-nowrap group ${
                      collapsed ? 'justify-center px-2' : ''
                    } ${
                      active
                        ? 'bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400'
                        : 'text-foreground-600 dark:text-foreground-400 hover:bg-background-100 dark:hover:bg-background-200 hover:text-foreground-900 dark:hover:text-foreground-200'
                    }`}
                    title={collapsed ? item.label : undefined}
                  >
                    {/* Active indicator bar */}
                    {active && (
                      <span className="absolute left-0 w-[3px] h-6 rounded-r-full bg-primary-500" />
                    )}
                    <div
                      className={`w-[22px] h-[22px] flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${
                        active ? '' : 'group-hover:scale-110'
                      }`}
                    >
                      <i
                        className={`${item.icon} text-lg ${
                          active ? 'text-primary-600 dark:text-primary-400' : 'text-foreground-400 dark:text-foreground-500 group-hover:text-foreground-600 dark:group-hover:text-foreground-300'
                        }`}
                      />
                    </div>
                    {!collapsed && (
                      <span className="leading-tight">{item.label}</span>
                    )}
                    {/* Collapsed tooltip */}
                    {collapsed && (
                      <span className="fixed left-[80px] bg-foreground-800 dark:bg-background-300 text-white dark:text-foreground-100 text-xs px-2.5 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-60 shadow-lg">
                        {item.label}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User mini-card */}
        {!collapsed && profile && (
          <div className="mx-3 mb-3 p-3 rounded-xl bg-background-50 dark:bg-background-200 border border-background-100 dark:border-background-300">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center text-primary-700 text-[11px] font-bold flex-shrink-0">
                {(profile.full_name || '').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-foreground-900 dark:text-foreground-100 truncate">{profile.full_name}</p>
                <p className="text-[11px] text-foreground-500 capitalize truncate">
                  {isTrial ? 'Trial · Grátis' : (profile.role === 'admin' ? 'Administrador' : 'Usuário')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Collapse toggle */}
        <div className="p-2.5 border-t border-background-100/80 dark:border-background-200/80">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-foreground-400 hover:bg-background-100 dark:hover:bg-background-200 hover:text-foreground-700 dark:hover:text-foreground-300 transition-all duration-200 cursor-pointer"
          >
            <i className={`text-lg ${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}`} />
            {!collapsed && <span className="text-[13px]">Recolher menu</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <Header
          profile={profile}
          onMenuClick={() => setMobileOpen(true)}
        />
        <main className="flex-1 overflow-y-auto p-5 md:p-7">
          <Outlet />
        </main>
      </div>
    </div>
  );
}